import * as R from 'ramda';
import { Action } from 'redux';
import { ActionType } from '../../core/redux/actionType';
import { AppState } from '../../core/redux/appState';
import { makeApiAction, makeRsaaTypes, makeApiReducer, ApiReducer } from '../../core/api/rsaa';
import { Endpoints } from '../../core/api/endpoints';


const KEY_ACTION = ActionType.PreEnrollECourseRequest;
const ACTIONS    = [
  ActionType.PreEnrollECourseRequest,
  ActionType.PreEnrollECourseResponse,
  ActionType.PreEnrollECourseError,
];


export type PreEnrollData = {
  enrolled: boolean;
  moodleUserId: number;
  courseId: string;
  scormModuleId: number;
};


export type PreEnrollStatus = {
  status: 'idle' | 'pending' | 'success' | 'error';
  data?: PreEnrollData;
  projectId?: number;
  participantId?: number;
};


type Payload = {
  enrolled: boolean;
  moodleUserId: number;
  courseId: string;
  scormModuleId: number;
};


type Meta = {
  projectId: number;
  participantId: number;
};


/**
 * Pre-runs the enrollment check (CreateMoodleUser + EnrollMoodleUser) so that the eventual
 * /eCourse/eCourseUrl call can skip it.
 */
export const preEnrollECourseAction = (
  projectId: number,
  participantId: number,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.PreEnrollECourse,
    headers:  { 'content-type': 'application/json', 'accept': 'application/json' },
    body:     JSON.stringify({ projectId, participantId }),
    types:    makeRsaaTypes<Payload, Meta>(ACTIONS, { projectId, participantId }),
    bailout:  state => {
      const s = state.eCourse?.preEnrollStatus;
      return s?.status === 'pending'
          && s?.projectId === projectId
          && s?.participantId === participantId;
    },
  },
);


export const resetPreEnrollECourseAction = (): Action => ({
  type: ActionType.ResetPreEnrollECourse,
});


const apiReducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const { projectId, participantId } = action.meta;

  if (isLoading) {
    return R.assocPath<PreEnrollStatus, AppState>(
      ['eCourse', 'preEnrollStatus'],
      { status: 'pending', projectId, participantId },
      state,
    );
  }

  // Drop stale responses: if the slot was reset or moved to a different project/participant
  // (e.g. user navigated away or component unmounted before the request returned), ignore.
  const current = state.eCourse?.preEnrollStatus;
  if (
    !current
    || current.status === 'idle'
    || current.projectId !== projectId
    || current.participantId !== participantId
  ) {
    return state;
  }

  if (errorMsg) {
    return R.assocPath<PreEnrollStatus, AppState>(
      ['eCourse', 'preEnrollStatus'],
      { status: 'error', projectId, participantId },
      state,
    );
  }

  const data: PreEnrollData = {
    enrolled:      payload?.enrolled ?? false,
    moodleUserId:  payload?.moodleUserId ?? 0,
    courseId:      payload?.courseId ?? '0',
    scormModuleId: payload?.scormModuleId ?? 0,
  };

  return R.assocPath<PreEnrollStatus, AppState>(
    ['eCourse', 'preEnrollStatus'],
    { status: 'success', data, projectId, participantId },
    state,
  );
};

const apiOnly = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, apiReducer);

export const preEnrollECourseReducer = (state: AppState, action: Action): AppState => {
  if (action.type === ActionType.ResetPreEnrollECourse) {
    return R.assocPath<PreEnrollStatus, AppState>(
      ['eCourse', 'preEnrollStatus'],
      { status: 'idle' },
      state,
    );
  }
  return apiOnly(state, action);
};
